import { useEffect, useRef, useState } from "react";
import { BatteryLow, Check, Target, Zap, Clock, ChevronDown, ChevronUp, RotateCcw, Play, Square, CheckCircle2 } from "lucide-react";

const BRAIN_OPTIONS = [
  { key: "ready", label: "Ready", hint: "Full plan" },
  { key: "struggling", label: "Struggling", hint: "Go gentle" },
  { key: "overwhelmed", label: "Overwhelmed", hint: "Low battery mode" },
];

// Optional "How is your brain today?" check-in. Records a state; Overwhelmed
// switches Today into Low Battery Mode.
export function BrainCheckIn({ value, onSelect }) {
  const [open, setOpen] = useState(!value);
  useEffect(() => { if (!value) setOpen(true); }, [value]);
  const current = BRAIN_OPTIONS.find((o) => o.key === value);

  if (value && !open) {
    return (
      <div className="flex items-center justify-between rounded-2xl border border-[#E6E9EE] bg-white px-4 py-3" data-testid="brain-checkin-chip">
        <p className="text-sm text-[#5A6472]">
          Brain today: <span className="font-bold text-[#0B132B]">{current ? current.label : "Set"}</span>
        </p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="brain-checkin-change"
          className="text-sm font-semibold text-[#15816B] underline-offset-2 hover:underline"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <section className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid="brain-checkin">
      <h2 className="font-heading text-base font-extrabold">How is your brain today?</h2>
      <p className="mt-1 text-xs text-[#8A94A6]">Optional. It only shapes today, nothing is tracked as a streak.</p>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {BRAIN_OPTIONS.map((o) => {
          const active = o.key === value;
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => { onSelect(o.key); setOpen(false); }}
              data-testid={`brain-checkin-${o.key}`}
              className={`flex flex-col items-center gap-0.5 rounded-xl border px-2 py-3 text-center transition-colors ${
                active ? "border-[#15816B] bg-[#EAF5F1]" : "border-[#D9DEE6] bg-[#FAFBFC] hover:border-[#15816B]/50"
              }`}
            >
              <span className="font-heading text-sm font-bold text-[#0B132B]">{o.label}</span>
              <span className="text-[11px] text-[#8A94A6]">{o.hint}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

// A gentle 5 to 10 minute focus timer. Fires onComplete when it finishes.
function FocusTimer({ onComplete }) {
  const [remaining, setRemaining] = useState(0);
  const [running, setRunning] = useState(false);
  const [minutes, setMinutes] = useState(0);
  const [finished, setFinished] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!running) return;
    ref.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(ref.current);
          setRunning(false);
          setFinished(true);
          onComplete(minutes);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(ref.current);
  }, [running, minutes, onComplete]);

  const start = (min) => { setMinutes(min); setRemaining(min * 60); setFinished(false); setRunning(true); };
  const stop = () => { if (ref.current) clearInterval(ref.current); setRunning(false); setRemaining(0); };

  return (
    <section className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid="low-battery-timer">
      <div className="flex items-center gap-2">
        <Clock className="h-5 w-5 text-[#3A7CA5]" aria-hidden="true" />
        <h2 className="font-heading text-base font-extrabold">Focus for a few minutes</h2>
      </div>
      {running ? (
        <div className="mt-4 flex items-center justify-between">
          <span className="font-heading text-4xl font-extrabold tabular-nums text-[#0B132B]" data-testid="low-battery-timer-count">{fmt(remaining)}</span>
          <button
            type="button"
            onClick={stop}
            data-testid="low-battery-timer-stop"
            className="inline-flex items-center gap-2 rounded-xl border border-[#D9DEE6] bg-white px-4 py-2.5 font-heading text-sm font-bold text-[#0B132B] transition-colors hover:bg-[#F1F4F8]"
          >
            <Square className="h-4 w-4" aria-hidden="true" /> Stop
          </button>
        </div>
      ) : finished ? (
        <div className="mt-4" data-testid="low-battery-timer-done">
          <p className="flex items-center gap-2 text-sm font-semibold text-[#15816B]">
            <CheckCircle2 className="h-5 w-5" aria-hidden="true" /> That counts as a full session today.
          </p>
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={() => start(5)} data-testid="low-battery-timer-5" className="rounded-xl border border-[#D9DEE6] bg-white px-4 py-2.5 text-sm font-bold text-[#0B132B] transition-colors hover:bg-[#F1F4F8]">5 more min</button>
            <button type="button" onClick={() => start(10)} data-testid="low-battery-timer-10" className="rounded-xl border border-[#D9DEE6] bg-white px-4 py-2.5 text-sm font-bold text-[#0B132B] transition-colors hover:bg-[#F1F4F8]">10 more min</button>
          </div>
        </div>
      ) : (
        <>
          <p className="mt-1 text-xs text-[#8A94A6]">Pick a short block. Start the one thing, that is the whole goal.</p>
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={() => start(5)} data-testid="low-battery-timer-5" className="inline-flex items-center gap-2 rounded-xl bg-[#0B132B] px-4 py-2.5 font-heading text-sm font-bold text-white transition-transform hover:-translate-y-0.5">
              <Play className="h-4 w-4" aria-hidden="true" /> 5 minutes
            </button>
            <button type="button" onClick={() => start(10)} data-testid="low-battery-timer-10" className="inline-flex items-center gap-2 rounded-xl border border-[#D9DEE6] bg-white px-4 py-2.5 font-heading text-sm font-bold text-[#0B132B] transition-colors hover:bg-[#F1F4F8]">
              <Play className="h-4 w-4" aria-hidden="true" /> 10 minutes
            </button>
          </div>
        </>
      )}
    </section>
  );
}

// Low Battery Mode: shrinks the day to Must Do, one Quick Win, a focus timer,
// and a collapsed Can Wait list.
export default function LowBatteryMode({ entry, onPriorityChange, onToggleWin, onWinTextChange, onExit, onResetDay, onHaptic, onCompleteAction }) {
  const [showCanWait, setShowCanWait] = useState(false);
  const wins = entry.quick_wins || [];
  const withText = wins.map((w, i) => ({ ...w, i })).filter((w) => (w.text || "").trim());
  const focus = withText.find((w) => !w.done) || withText[0] || { i: 0, text: wins[0]?.text || "", done: wins[0]?.done || false };
  const canWaitWins = withText.filter((w) => w.i !== focus.i);
  const blocks = entry.time_blocks || [];

  const toggleFocus = () => {
    const becomingDone = !wins[focus.i]?.done;
    onToggleWin(focus.i);
    if (becomingDone) onCompleteAction({ action: "quick_win", brain_state: "overwhelmed" });
  };

  return (
    <div className="space-y-5" data-testid="low-battery-mode">
      <div className="sticky top-[56px] z-10 rounded-2xl border border-[#CFE0EE] bg-[#EEF4FA] p-4 shadow-sm" data-testid="low-battery-banner">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <BatteryLow className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#3A7CA5]" aria-hidden="true" />
            <div>
              <p className="font-heading text-sm font-extrabold text-[#0B132B]">Low battery mode</p>
              <p className="mt-0.5 text-xs text-[#5A6472]">Forget the whole list for a moment. Let's get one thing moving.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onExit}
            data-testid="low-battery-exit"
            className="flex-shrink-0 whitespace-nowrap rounded-lg border border-[#3A7CA5]/40 bg-white px-3 py-2 font-heading text-xs font-bold text-[#0B132B] transition-colors hover:bg-[#F1F4F8]"
          >
            Return to full plan
          </button>
        </div>
      </div>

      <section className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid="low-battery-must-do">
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5 text-[#15816B]" aria-hidden="true" />
          <h2 className="font-heading text-base font-extrabold">Must do</h2>
        </div>
        <p className="mt-1 text-xs text-[#8A94A6]">Just the one thing. Nothing else needs an answer right now.</p>
        <textarea
          value={entry.priority}
          onChange={(e) => onPriorityChange(e.target.value)}
          rows={2}
          maxLength={280}
          placeholder="What is the one thing?"
          data-testid="low-battery-must-do-input"
          className="mt-3 w-full resize-none rounded-xl border border-[#D9DEE6] bg-[#FAFBFC] px-4 py-3 text-base leading-snug outline-none focus:border-[#15816B] focus:ring-2 focus:ring-[#15816B]/20"
        />
      </section>

      <section className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid="low-battery-quick-win">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-[#F5A623]" aria-hidden="true" />
          <h2 className="font-heading text-base font-extrabold">One quick win</h2>
        </div>
        <p className="mt-1 text-xs text-[#8A94A6]">One tiny finish. That is a full session on a hard day.</p>
        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            onClick={toggleFocus}
            aria-label={wins[focus.i]?.done ? "Mark not done" : "Mark done"}
            data-testid="low-battery-quick-win-check"
            className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border transition-colors ${
              wins[focus.i]?.done ? "border-[#15816B] bg-[#15816B] text-white" : "border-[#CBD3DE] bg-white text-transparent"
            }`}
          >
            <Check className="h-4 w-4" aria-hidden="true" />
          </button>
          <input
            type="text"
            value={wins[focus.i]?.text || ""}
            onChange={(e) => onWinTextChange(focus.i, e.target.value)}
            maxLength={140}
            placeholder="One small win"
            data-testid="low-battery-quick-win-input"
            className={`w-full border-b border-transparent bg-transparent py-1.5 text-base outline-none focus:border-[#15816B] ${
              wins[focus.i]?.done ? "text-[#8A94A6] line-through" : "text-[#0B132B]"
            }`}
          />
        </div>
      </section>

      <FocusTimer onComplete={(min) => { if (onHaptic) onHaptic(); onCompleteAction({ action: "focus_timer", minutes: min, brain_state: "overwhelmed" }); }} />

      {(canWaitWins.length > 0 || blocks.length > 0) && (
        <section className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid="low-battery-can-wait">
          <button
            type="button"
            onClick={() => setShowCanWait((v) => !v)}
            data-testid="low-battery-can-wait-toggle"
            className="flex w-full items-center justify-between text-left"
          >
            <span className="font-heading text-base font-extrabold text-[#8A94A6]">Can wait</span>
            {showCanWait ? <ChevronUp className="h-4 w-4 text-[#8A94A6]" aria-hidden="true" /> : <ChevronDown className="h-4 w-4 text-[#8A94A6]" aria-hidden="true" />}
          </button>
          {showCanWait && (
            <div className="mt-3 space-y-2 text-sm text-[#8A94A6]" data-testid="low-battery-can-wait-list">
              {canWaitWins.map((w) => (
                <p key={`w-${w.i}`} className={w.done ? "line-through" : ""}>• {w.text}</p>
              ))}
              {blocks.map((b, bi) => (
                <p key={`b-${bi}`}>{b.time ? `${b.time} · ` : ""}{b.label}</p>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid="low-battery-actions">
        <button
          type="button"
          onClick={onResetDay}
          data-testid="low-battery-reset"
          className="inline-flex items-center gap-2 rounded-xl border border-[#D9DEE6] bg-white px-4 py-2.5 font-heading text-sm font-bold text-[#0B132B] transition-colors hover:bg-[#F1F4F8]"
        >
          <RotateCcw className="h-4 w-4 text-[#15816B]" aria-hidden="true" /> Reset the day
        </button>
      </section>
    </div>
  );
}
